/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";

const COLORS = { ink: "#374049", teal: "#01767A" };
const REGION = "southamerica-east1";

function api(name) { return httpsCallable(getFunctions(undefined, REGION), name); }

function NoticeIndex({ uid }) {
  const [notices, setNotices] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!uid) return undefined;
    const ref = query(collection(db, "users", uid, "notifications"), where("type", "==", "taxonomic_split"));
    return onSnapshot(ref,
      (snap) => { setNotices(snap.docs.map((item) => ({ id: item.id, ...item.data() }))); setError(""); },
      () => setError("Could not load your taxonomic review notifications."));
  }, [uid]);
  const ordered = useMemo(() => [...notices].sort((a, b) =>
    (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)), [notices]);
  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-4 py-8 text-[#374049]">
      <header>
        <Link to="/" className="text-sm font-semibold text-[#01767A] hover:underline">← Home</Link>
        <h1 className="mt-3 text-4xl" style={{ fontFamily: "Cormorant Garamond" }}>Taxonomic reviews</h1>
        <p className="mt-2 text-sm text-[#374049]/75">Species splits can require you to reidentify individual photographs. You can return here at any time.</p>
      </header>
      {error && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {ordered.length === 0 && !error && <div className="rounded-2xl border border-[#93CA87]/50 bg-white p-6 text-sm">You have no species-split reviews at this time.</div>}
      {ordered.map((notice) => (
        <Link key={notice.id} to={`/taxonomy/review/${encodeURIComponent(notice.revisionId)}`}
          className="block rounded-2xl border border-[#93CA87]/60 bg-white p-5 shadow-sm transition hover:border-[#24C3BE]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-semibold">{notice.sourceNames?.join(", ") || "Species split"}</h2>
            <span className="rounded-full bg-[#93CA87]/20 px-3 py-1 text-xs font-medium">{notice.recoveryStatus === "restored" ? "Administrator recovery" : notice.reviewStatus === "complete" ? "Completed" : "Review photographs"}</span>
          </div>
          <p className="mt-2 text-sm text-[#374049]/75">{(notice.resultingSpecies || []).map((item) => item.scientificName).join(" / ")}</p>
          <p className="mt-2 text-xs text-[#374049]/60">{notice.affectedPhotographs || 0} photograph(s) originally affected · Open review →</p>
        </Link>
      ))}
    </div>
  );
}

function ChoiceButton({ selected, disabled, children, onClick }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      aria-pressed={selected}
      className={`w-full rounded-xl border px-4 py-3 text-left text-sm transition disabled:opacity-50 ${selected
        ? "border-[#01767A] bg-[#24C3BE]/15 ring-1 ring-[#01767A]"
        : "border-[#93CA87]/45 bg-white hover:border-[#01767A]"}`}>
      {children}
    </button>
  );
}

function ReviewDetails({ uid, revisionId }) {
  const [review, setReview] = useState(null);
  const [choices, setChoices] = useState({});
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const response = await api("getTaxonomicSplitReview")({ revisionId });
      const data = response.data;
      setReview(data);
      setChoices(Object.fromEntries((data.selections || []).map((entry) => [entry.photoKey, entry])));
      setIndex((value) => Math.min(value, Math.max(0, (data.photos || []).length - 1)));
    } catch (err) {
      setError(err?.message || "The review could not be loaded.");
    } finally { setLoading(false); }
  }

  useEffect(() => {
    let alive = true;
    setReview(null); setLoading(true); setError(""); setIndex(0);
    api("getTaxonomicSplitReview")({ revisionId }).then((response) => {
      if (!alive) return;
      const data = response.data;
      setReview(data);
      setChoices(Object.fromEntries((data.selections || []).map((entry) => [entry.photoKey, entry])));
      setLoading(false);
    }).catch((err) => { if (alive) { setError(err?.message || "Could not load review."); setLoading(false); } });
    return () => { alive = false; };
  }, [uid, revisionId]);

  const photos = review?.photos || [];
  const current = photos[index] || null;
  const choice = current ? choices[current.photoKey] : null;
  const selectedCount = photos.filter((photo) => !!choices[photo.photoKey]).length;
  const canEdit = (review?.reviewStatus === "review_pending" || review?.reviewStatus === "review_in_progress") && !applying;

  async function applyReview() {
    if (!review || !canEdit || applying || saving || selectedCount !== photos.length || !photos.length || !confirmed) return;
    setApplying(true); setError(""); setMessage("");
    try {
      const response = await api("applyTaxonomicSplitReview")({
        revisionId, fingerprint: review.fingerprint, confirmApply: true,
      });
      setConfirmed(false);
      await load();
      setMessage(response.data?.consistencyStatus === "pending"
        ? "Your identifications were applied. Collection statistics are awaiting reconciliation; no photographs need to be submitted again."
        : "Review submitted. Your identifications have been applied.");
    } catch (err) {
      setError(err?.message || "Your review could not be submitted. No photographs were moved. Reload and try again.");
    } finally { setApplying(false); }
  }

  async function save(decision, scientificName = null) {
    if (!current || !canEdit || saving) return;
    setSaving(true); setConfirmed(false); setError(""); setMessage("");
    try {
      await api("saveTaxonomicSplitSelection")({
        revisionId, photoKey: current.photoKey, fingerprint: review.fingerprint,
        decision, ...(scientificName ? { scientificName } : {}),
      });
      setChoices((previous) => {
        const next = { ...previous };
        if (decision === "clear") delete next[current.photoKey];
        else next[current.photoKey] = { photoKey: current.photoKey, decision, scientificName };
        return next;
      });
      setMessage("Choice saved. You can submit all identifications when every photograph has been reviewed.");
    } catch (err) {
      setError(err?.message || "Could not save this photograph's selection. Please reload the review.");
    } finally { setSaving(false); }
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 px-3 py-6 text-[#374049] sm:px-6">
      <header className="space-y-2">
        <Link to="/taxonomy/reviews" className="text-sm font-semibold text-[#01767A] hover:underline">← All taxonomic reviews</Link>
        <h1 className="text-4xl sm:text-5xl" style={{ fontFamily: "Cormorant Garamond" }}>Photograph identification review</h1>
        {review && <p className="text-sm">Original species: <em className="font-semibold">{review.originalScientificName}</em></p>}
      </header>
      {loading && <p role="status" className="rounded-2xl bg-white p-5">Loading photographs…</p>}
      {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error} <button type="button" onClick={load} className="ml-2 font-semibold underline">Reload</button></div>}
      {review && !loading && (
        <>
          <section className="rounded-2xl border border-[#93CA87]/50 bg-white p-4 sm:p-6">
            <p className="text-sm leading-relaxed">{review.explanation || "This species has been divided into multiple accepted species. Choose an identification for each photograph."}</p>
            <p className="mt-2 text-sm text-[#374049]/70">Geographic distributions and identification notes are <strong>guidance only</strong>. Your location will never determine the choice automatically.</p>
            {review.reference?.url && /^https?:\/\//.test(review.reference.url) && (
              <a href={review.reference.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-xs font-semibold text-[#01767A] underline">Taxonomic reference ↗</a>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-semibold">{selectedCount} of {photos.length} photographs reviewed</span>
              <span className="text-xs text-[#374049]/65">Selections are saved as drafts. Photographs move only after you submit your completed review.</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#374049]/10" aria-label="Review progress">
              <div className="h-full rounded-full bg-[#24C3BE]" style={{ width: `${photos.length ? 100 * selectedCount / photos.length : 0}%` }} />
            </div>
          </section>
          {review.restoredByAdministrator && (
            <section role="status" className="rounded-xl border border-amber-300 bg-amber-50 p-5 text-sm text-amber-900">
              An administrator has restored your previous species records while correcting a taxonomic issue. The earlier photograph reidentifications are no longer active. No action is required on this review page.
            </section>
          )}
          {review.classificationsApplied && (
            <section className="space-y-2 rounded-xl border border-[#93CA87] bg-white p-5 text-sm">
              <h2 className="text-lg font-semibold">Review completed</h2>
              <p>Your classifications have been applied. Your photographs, dates, locations and notes were retained.</p>
              {review.application && (
                <>
                  <p><strong>Photographs reviewed:</strong> {review.application.originalCount || 0} · <strong>Reidentified:</strong> {review.application.classifiedCount || 0} · <strong>Uncertain:</strong> {review.application.uncertainCount || 0}</p>
                  {(review.application.destinationBreakdown || []).map((item) => (
                    <p key={item.scientificName}><em>{item.scientificName}</em>: {item.count} photograph(s)</p>
                  ))}
                  {review.application.uncertainCount > 0 && <p className="text-[#374049]/75">Uncertain photographs remain in your collection under the original identification, pending future reassessment.</p>}
                </>
              )}
            </section>
          )}
          {photos.length === 0 && !review.classificationsApplied && !review.restoredByAdministrator && <p className="rounded-xl bg-white p-5 text-sm">There are no photographs remaining under the original species.</p>}
          {current && (
            <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <section className="overflow-hidden rounded-2xl border border-[#93CA87]/50 bg-white">
                <div className="flex min-h-56 items-center justify-center bg-[#374049]/5 p-3 sm:min-h-80">
                  {current.url ? <img src={current.url} alt={current.label || `Photograph ${index + 1}`} className="max-h-[26rem] max-w-full object-contain" /> : <span className="text-sm">Photograph preview unavailable</span>}
                </div>
                <div className="space-y-2 p-4 text-xs sm:p-5">
                  <p className="font-semibold">Photograph {index + 1} of {photos.length}{current.label ? ` · ${current.label}` : ""}</p>
                  {current.date && <p><strong>Date: </strong>{current.date}</p>}
                  {(current.location || current.country || current.adminArea || current.city) && <p><strong>Location: </strong>{[current.location, current.city, current.adminArea, current.country].filter(Boolean).join(", ")}</p>}
                  {current.variant && <p><strong>Form: </strong>{current.variant}</p>}
                  {current.notes && <p className="whitespace-pre-wrap"><strong>Notes: </strong>{current.notes}</p>}
                </div>
              </section>
              <section className="space-y-3 rounded-2xl border border-[#93CA87]/50 bg-[#FCFFF6] p-4 sm:p-5">
                <h2 className="text-xl font-semibold" style={{ fontFamily: "Cormorant Garamond" }}>Which species is this?</h2>
                <p className="text-xs text-[#374049]/70">Inspect the photograph and decide for yourself. You may change any saved choice before the final reidentification process.</p>
                {(review.resultingSpecies || []).map((species) => (
                  <ChoiceButton key={species.scientificName} disabled={!canEdit || saving}
                    selected={choice?.decision === "species" && choice?.scientificName === species.scientificName}
                    onClick={() => save("species", species.scientificName)}>
                    <span className="font-semibold italic">{species.scientificName}</span>
                    {species.commonName && <span className="ml-1 text-[#374049]/65">({species.commonName})</span>}
                    {species.distribution && <span className="mt-2 block text-xs"><strong>Distribution:</strong> {species.distribution}</span>}
                    {species.identificationNotes && <span className="mt-2 block text-xs"><strong>Identification:</strong> {species.identificationNotes}</span>}
                  </ChoiceButton>
                ))}
                <ChoiceButton selected={choice?.decision === "uncertain"} disabled={!canEdit || saving} onClick={() => save("uncertain")}>
                  <strong>Uncertain identification</strong>
                  <span className="mt-1 block text-xs text-[#374049]/70">I cannot confidently assign this photograph to either resulting species.</span>
                </ChoiceButton>
                {choice && canEdit && <button type="button" disabled={saving} onClick={() => save("clear")} className="text-xs font-semibold text-[#01767A] underline disabled:opacity-50">Clear this choice</button>}
                {saving && <p role="status" className="text-xs text-[#01767A]">Saving your choice…</p>}
                {message && !saving && <p role="status" className="text-xs text-[#01767A]">{message}</p>}
              </section>
            </div>
          )}
          {photos.length > 0 && (
            <nav aria-label="Photograph review navigation" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#93CA87]/50 bg-white p-4">
              <button type="button" disabled={index === 0} onClick={() => { setIndex((value) => value - 1); setMessage(""); }} className="rounded-xl border border-[#93CA87] px-5 py-2 text-sm font-semibold disabled:opacity-40">← Previous</button>
              <span className="text-sm font-semibold">{index + 1} / {photos.length}</span>
              <button type="button" disabled={index >= photos.length - 1} onClick={() => { setIndex((value) => value + 1); setMessage(""); }} className="rounded-xl bg-[#01767A] px-5 py-2 text-sm font-semibold text-white disabled:opacity-40">Next →</button>
            </nav>
          )}
          {photos.length > 0 && selectedCount === photos.length && !review.classificationsApplied && (
            <section className="space-y-3 rounded-xl border border-[#93CA87] bg-white p-5 text-sm">
              <h2 className="text-lg font-semibold">All identification choices saved</h2>
              <p>Submitting moves photographs assigned to a resulting species into that species in your collection. Uncertain photographs stay under the original name. Your photograph files, dates, locations, notes and other observation metadata are retained.</p>
              <p>Once applied, these saved decisions are final in this review. You cannot undo them using this page.</p>
              <label className="flex cursor-pointer items-start gap-3">
                <input type="checkbox" checked={confirmed} disabled={applying || saving}
                  onChange={(event) => setConfirmed(event.target.checked)} className="mt-1 accent-[#01767A]" />
                <span>I have reviewed every photograph and want BioIndex to apply these identifications.</span>
              </label>
              <button type="button" onClick={applyReview} disabled={!confirmed || applying || saving}
                className="rounded-xl bg-[#01767A] px-5 py-3 font-semibold text-white disabled:opacity-45">
                {applying ? "Applying identifications…" : "Submit and apply identifications"}
              </button>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default function TaxonomicReviewPage({ uid }) {
  const { revisionId } = useParams();
  return (
    <main className="min-h-screen bg-[#FCFFF6]" style={{ color: COLORS.ink }}>
      {revisionId ? <ReviewDetails key={`${uid}-${revisionId}`} uid={uid} revisionId={revisionId} /> : <NoticeIndex uid={uid} />}
    </main>
  );
}
